import { useCallback, useEffect, useState } from 'react'
import { Divider } from 'antd'
import { useInterval } from 'ahooks'

import { API } from '@/api'
import EditIcon from '@/assets/icons/edit.svg?react'
import { Trans } from '@/components'
import { Button } from '@/components/base/Button'
import { PatchOrderType } from '@/consts/order'
import { useAppContext } from '@/hooks/useAppContext'
import { MachineBill } from '@/models/bill'
import { Instance } from '@/models/instance'
import { formatDatePreset, formatMillicentPrice, formatPrice } from '@/utils/formatter'
import { useNavigate } from 'react-router-dom'

import { ExpirationBehaviorModal } from './ExpirationBehaviorModal'
import { UpdateToggle } from './UpdateToggle'

interface InstanceBillingProps {
  instance: Instance
  teamResID?: string
  onInstanceUpdated?: () => void
}

export function InstanceBilling({ instance, teamResID, onInstanceUpdated }: InstanceBillingProps) {
  const navigate = useNavigate()
  const { isOnTeamPage } = useAppContext()
  const [bills, setBills] = useState<MachineBill[]>([])
  const [changingRenew, setChangingRenew] = useState(false)
  const [openExpirationBehaviorModal, setOpenExpirationBehaviorModal] = useState(false)
  const [_loading, setLoading] = useState(true)
  const [initialized, setInitialized] = useState(false)

  const getData = useCallback(async () => {
    try {
      let res
      if (teamResID) {
        // Team mode: use new API
        res = await API.getTeamMemberInstanceBills({
          res_id: teamResID,
          inst_res_id: instance.resID,
        })
      } else {
        // Platform mode: use original API
        res = await API.getNodeBill({ request_id: instance.displayID })
      }
      if (res.code === 0) {
        setBills(res.bills.map((bill) => new MachineBill(bill)))
      }
    } catch (err) {
      console.error(err)
    } finally {
      if (!initialized) {
        setLoading(false)
        setInitialized(true)
      }
    }
  }, [instance.displayID, teamResID])

  useInterval(
    () => {
      getData()
    },
    instance.isReleased ? undefined : 10000,
    { immediate: true },
  )

  useEffect(() => {
    if (instance.isReleased) {
      getData()
    }
  }, [instance.displayID, instance.isReleased, getData])

  const handleAutoRenewalChange = async (checked: boolean) => {
    if (!instance.order) return
    if (changingRenew) return

    setChangingRenew(true)

    try {
      await API.updateOrder({
        type: PatchOrderType.AutoRenew,
        from_order_id: instance.order.id,
        auto_renewal: checked,
      })

      onInstanceUpdated?.()
    } catch (err) {
      console.error(err)
    } finally {
      setChangingRenew(false)
    }
  }

  const handleExpirationBehaviorChange = async (checked: boolean) => {
    if (!instance.order) return
    if (changingRenew) return

    setChangingRenew(true)

    try {
      await API.updateOrder({
        type: PatchOrderType.OrderToBilling,
        from_order_id: instance.order.id,
        switch_on: checked,
      })

      onInstanceUpdated?.()
    } catch (err) {
      console.error(err)
    } finally {
      setChangingRenew(false)
    }
  }

  const handleCloseExpirationBehaviorModal = () => {
    setOpenExpirationBehaviorModal(false)
  }
  return (
    <div className="bg-panel-gray-100 rounded-lg flex flex-col py-4 t-sm text-tertiary gap-y-4">
      {bills.map((bill) => (
        <div key={bill.displayID} className="grid w-full grid-cols-5">
          <div className="px-4">
            <div className="text-secondary">
              <Trans i18nKey="instance.detail.billing.startTime">Start Time</Trans>
            </div>
            <div className="text-primary">{formatDatePreset(bill.startTime, 'minute')}</div>
          </div>

          <div className="px-4">
            <div className="text-secondary">
              <Trans i18nKey="instance.detail.billing.endTime">End Time</Trans>
            </div>
            <div className="text-primary">
              {bill.isRunning ? (
                <div className="text-success">Running</div>
              ) : (
                formatDatePreset(bill.endTime, 'minute')
              )}
            </div>
          </div>

          <div className="px-4">
            <div className="text-secondary">
              {bill.billType === 'Hourly' && (
                <Trans i18nKey="instance.detail.billing.priceHour">Price/Hour</Trans>
              )}
              {bill.billType === 'Daily' && (
                <Trans i18nKey="instance.detail.billing.priceDay">Price/Day</Trans>
              )}
              {bill.billType === 'Monthly' && (
                <Trans i18nKey="instance.detail.billing.priceMonth">Price/Month</Trans>
              )}
              {bill.billType !== 'Hourly' &&
                bill.billType !== 'Daily' &&
                bill.billType !== 'Monthly' && (
                  <Trans i18nKey="instance.detail.billing.price">Price</Trans>
                )}
            </div>
            <div className="text-primary">
              {formatMillicentPrice(
                bill.priceMillicent,
                false,
                bill.priceMillicent > 100000 ? 'Credits' : 'Credit',
              )}
            </div>
          </div>

          <div className="px-4">
            <div className="text-secondary">
              <Trans i18nKey="instance.detail.billing.cost">Cost</Trans>
            </div>
            <div className="text-primary">
              {formatPrice(
                Math.abs(bill.amount),
                false,
                Math.abs(bill.amount) > 100 ? 'Credits' : 'Credit',
              )}
            </div>
          </div>

          <div className="px-4">
            <div className="text-secondary">
              <Trans i18nKey="instance.detail.billing.duration">Duration</Trans>
            </div>
            <div className="text-primary">{bill.usedTimeStr}</div>
          </div>

          <div className="px-4 mt-2">
            <div className="text-secondary">
              <Trans i18nKey="instance.detail.billing.billingMethod">Billing Method</Trans>
            </div>
            <div className="text-primary">
              {bill.billingMethodLabel === 'Subscription' && (
                <Trans i18nKey="instance.detail.billing.subscription">Subscription</Trans>
              )}
              {bill.billingMethodLabel === 'Pay-as-you-go' && (
                <Trans i18nKey="instance.detail.billing.payAsYouGo">Pay-as-you-go</Trans>
              )}
            </div>
          </div>

          <div className="px-4 mt-2">
            <div className="text-secondary">
              <Trans i18nKey="instance.detail.billing.discount">Discount</Trans>
            </div>
            <div className="text-primary">
              {bill.discountLabel === 'No Discount' && (
                <Trans i18nKey="instance.detail.billing.noDiscount">No Discount</Trans>
              )}
              {bill.discountLabel !== 'No Discount' && (
                <Trans i18nKey="instance.detail.billing.discount">Discount</Trans>
              )}
            </div>
          </div>

          {instance.isSubscription && instance.canRelease && (
            <div className="px-4 mt-2">
              <div className="text-secondary">
                <Trans i18nKey="instance.detail.billing.autoRenewal">Auto Renewal</Trans>
              </div>
              <div className="text-primary">
                <UpdateToggle
                  type="autoRenewal"
                  value={instance.autoRenewal}
                  onChange={handleAutoRenewalChange}
                />
              </div>
            </div>
          )}

          {instance.isSubscription && instance.canRelease && !instance.autoRenewal && (
            <div>
              <div className="px-4 mt-2">
                <div className="text-secondary">
                  <Trans i18nKey="instance.detail.billing.expirationBehavior">
                    Expiration Behavior
                  </Trans>
                </div>
                <div className="text-primary flex items-start gap-1">
                  {instance.orderToBilling ? (
                    <Trans i18nKey="instance.detail.billing.switchToPayAsYouGo">
                      Switch to pay-as-you-go
                    </Trans>
                  ) : (
                    <Trans i18nKey="instance.detail.billing.releaseInstance">
                      Release Instance
                    </Trans>
                  )}
                  <Button onClick={() => setOpenExpirationBehaviorModal(true)} className="p-0">
                    <EditIcon className="w-5 h-5 text-highlight" />
                  </Button>
                  {/* <UpdateToggle
                      type="expirationBehavior"
                      value={instance.orderToBilling}
                      onChange={handleExpirationBehaviorChange}
                    /> */}
                </div>
              </div>
            </div>
          )}
          <ExpirationBehaviorModal
            open={openExpirationBehaviorModal}
            onClose={handleCloseExpirationBehaviorModal}
            onChange={handleExpirationBehaviorChange}
            value={instance.orderToBilling}
          />
        </div>
      ))}
      <div className="px-4">
        <Divider className="mb-4" />
        <div
          className="t-x-sb cursor-pointer text-btn-secondary"
          onClick={() => {
            const billingPath = isOnTeamPage ? '/team/billing?catalog=1' : '/billing?catalog=1'
            navigate(billingPath)
          }}
        >
          <Trans i18nKey="instance.detail.billing.viewBillingHistory">View Billing History</Trans>
        </div>
      </div>
    </div>
  )
}
